<script lang="ts" setup>
  import { useChatStore, type Message as ChatMessage } from '@/store/chat'
  import TieredMenu from 'primevue/tieredmenu'
  import { useAgent } from '@/shared/composables/useAgent'
  import { useChatFormatter } from '@/shared/composables/useChatFormatter'
  import { ref, onMounted, nextTick, watch, computed } from 'vue'
  import { storeToRefs } from 'pinia'
  import { useWidgetResolver } from '@/shared/composables/useWidgetResolver'
  import TemporaryUploadFile from '@/shared/dtos/TemporaryFiles'
  import { WidgetAction } from './types/WidgetAction'

  const newMessage = ref('')
  const messagesContainer = ref()
  const isLoading = ref(false)
  const chatStore = useChatStore()
  const { messages } = storeToRefs(chatStore)
  const menu = ref()

  const menuItems = ref([
    {
      label: 'Restaurar Chat',
      icon: 'pi pi-refresh',
      command: () => {
        clearChat()
      },
    },
  ])

  const toggleMenu = (event: any) => {
    menu.value.toggle(event)
  }

  const clearChat = () => {
    chatStore.clearMessages()
  }

  const handleSendMessage = async () => {
    const userMessage = newMessage.value.trim()
    if (userMessage === '' || isLoading.value) return

    // Add user message to the store
    chatStore.addMessage({
      sender: 'user',
      text: userMessage,
      type: 'text',
      timestamp: new Date().toISOString(),
    })

    newMessage.value = ''
    isLoading.value = true
    scrollToBottom()

    try {
      const agentResponse = await sendMessageToAgent(userMessage)
      await getAgentResponse(agentResponse)
    } catch (error) {
      console.error('Error getting agent response:', error)
      chatStore.addMessage({
        sender: 'assistant',
        text: 'Lo siento, hubo un error al procesar tu mensaje. Por favor intenta de nuevo.',
        type: 'text',
        timestamp: new Date().toISOString(),
      })
    } finally {
      isLoading.value = false
      scrollToBottom()
    }
  }

  const sendMessageToAgent = async (userMessage: string) => {
    const startTime = Date.now()
    const agentResponse = await useAgent(userMessage)
    const endTime = Date.now()

    const minDelay = 500
    const actualDelay = endTime - startTime
    if (actualDelay < minDelay) {
      await new Promise((resolve) =>
        setTimeout(resolve, minDelay - actualDelay)
      )
    }

    return agentResponse
  }

  const getAgentResponse = async (agentResponse: string) => {
    const { formattedText, json } = await useChatFormatter(agentResponse)

    chatStore.addMessage({
      sender: 'assistant',
      text: formattedText,
      json: json || undefined,
      type: 'text',
      timestamp: new Date().toISOString(),
    })
  }

  const scrollToBottom = () => {
    if (messagesContainer.value) {
      setTimeout(() => {
        messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
      }, 100)
    }
  }

  const onSaveLicenceType = async (licenseType: string) => {
    console.log('License Type Saved:', licenseType)
    const response = await sendMessageToAgent(licenseType)
    getAgentResponse(response)
  }

  const handleFilesUploaded = async (files: TemporaryUploadFile[]) => {
    await uploadedFiles(files)
  }

  const uploadedFiles = async (files: TemporaryUploadFile[]) => {
    if (!files || files.length === 0) return

    const fileMessage = files.map((file) => file.url).join(', ')
    const response = await sendMessageToAgent(fileMessage)
    getAgentResponse(response)
  }

  const handleWidgetAction = async (e: WidgetAction) => {
    console.log('Widget action:', e)

    switch (e.action) {
      case 'saveLicense':
        await onSaveLicenceType(e.payload.licenseType)
        break

      case 'onUpload':
        await handleFilesUploaded(e.payload.files)
        break
      // Handle other widget actions here
      default:
        console.warn('Unhandled action:', e.action)
    }
  }

  // Create a computed property to resolve widgets for all messages
  const messagesWithWidgets = computed(() => {
    return messages.value.map((message) => ({
      ...message,
      widgets: useWidgetResolver(message),
    }))
  })

  watch(
    [messages, isLoading],
    () => {
      nextTick(() => {
        scrollToBottom()
      })
    },
    { deep: true }
  )

  onMounted(scrollToBottom)
</script>

<template>
  <div class="chat-container">
    <div
      class="flex flex-row justify-content-between items-center p-2 text-primary border-bottom-1 border-gray-300 shadow-2"
      style="background: #dedfe3"
    >
      <div style="width: 48px"></div>
      <!-- Spacer -->
      <div class="flex align-self-center">
        <img
          src="@/assets/apsi-logo.png"
          alt="Apsi Logo"
          width="48"
          height="48"
          class="rounded-full shadow"
        />
      </div>
      <div class="relative">
        <Button
          icon="pi pi-ellipsis-v"
          class="p-button-rounded p-button-text p-button-primary"
          @click="toggleMenu"
          aria-haspopup="true"
          aria-controls="overlay_tmenu"
        ></Button>
        <TieredMenu ref="menu" id="overlay_tmenu" :model="menuItems" popup />
      </div>
    </div>
    <div class="chat-messages" ref="messagesContainer">
      <div
        v-for="(message, index) in messagesWithWidgets"
        :key="index"
        :class="['message mt-2', message.sender]"
      >
        <div class="message-content">
          <!-- Use the pre-resolved widget -->
          <template v-if="message?.widgets.length > 0">
            <component
              v-for="(widget, index) in message.widgets"
              :is="widget.component"
              v-bind="widget.props"
              @onWidgetAction="(action: WidgetAction) => handleWidgetAction(action)"
            />
          </template>
          <!-- Render text if it's not a widget-only message -->
          <div v-else v-html="message.text"></div>

          <!-- Handle cases where a query returns no data -->
          <div
            v-if="message.json?.data && message.json?.data.length === 0"
            class="text-gray-500"
          >
            No se encontraron datos relacionados con tu consulta.
          </div>
        </div>
      </div>
      <div v-if="isLoading" class="skeleton-container">
        <Skeleton
          height="2rem"
          class="mb-2 w-8 mt-2"
          style="background-color: #d1d5db"
        ></Skeleton>
      </div>
    </div>
    <div class="chat-input">
      <InputText
        class="text-sm font-ligth"
        v-model="newMessage"
        @keydown.enter="handleSendMessage"
        placeholder="Escribe tu consulta..."
        :disabled="isLoading"
      />
      <Button
        :disabled="isLoading || newMessage.trim() === ''"
        class="text-sm font-light"
        label="Enviar"
        @click="handleSendMessage"
      >
        <i class="pi pi-send text-sm"></i>
      </Button>
    </div>
  </div>
</template>

<style scoped>
  .chat-container {
    display: flex;
    flex-direction: column;
    height: 600px;
    width: 100%;
    /* Adjust as needed */
    border: 1px solid #ccc;
    border-radius: 8px;
    box-shadow: 0px 2px 18px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    background-color: #e7e7e7;
  }

  .chat-messages {
    flex-grow: 1;
    padding: 10px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    font-size: 14px;
    /* Custom scrollbar styles */
    scrollbar-width: thin;
    scrollbar-color: #b0b0b0 #e7e7e7;
    background: transparent;
  }

  /* Chrome, Edge, and Safari */
  .chat-messages::-webkit-scrollbar {
    width: 8px;
    background: transparent;
  }

  .chat-messages::-webkit-scrollbar-thumb {
    background: #b0b0b0;
    border-radius: 6px;
    border: 2px solid #e7e7e7;
  }

  .chat-messages::-webkit-scrollbar-track {
    background: transparent;
  }

  .message {
    padding: 8px 12px;
    margin-bottom: 5px;
    border-radius: 5px;
    max-width: 80%;
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
  }

  .message.user {
    background-color: #e0f7fa;
    color: #333;
    /* Light blue */
    align-self: flex-end;
  }

  .message.server {
    background-color: #f5f5f5;
    /* Light gray */
    align-self: center;
  }

  .message.assistant {
    background-color: #f2f2f2;
    color: #333;
    /* Light orange */
    align-self: flex-start;
  }

  .message-content {
    line-height: 1.4;
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
    max-width: 100%;
    padding: 5px;
  }

  .message-content strong {
    font-weight: 600;
    color: #2563eb;
    word-wrap: break-word;
  }

  .list-item {
    margin-bottom: 8px;
    padding-left: 8px;
    border-left: 2px solid #e5e7eb;
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
    max-width: 100%;
  }

  .list-number {
    font-weight: 600;
    color: #374151;
    margin-right: 4px;
    white-space: nowrap;
  }

  .skeleton-container {
    align-self: flex-start;
    margin-top: 5px;
    margin-bottom: 5px;
    width: 80%;
  }

  .chat-input {
    display: flex;
    padding: 10px;
    border-top: 1px solid #ccc;
  }

  .chat-input .p-inputtext {
    flex-grow: 1;
    margin-right: 10px;
  }
</style>
